import React, { useState, useEffect, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCatalog } from '../context/CatalogContext';
import { canAccessAthleteDetail } from '../lib/athleteDetail';
import { displayName, buildMeasurableGroups } from '../lib/publicProfile';
import VideoEmbed from '../components/VideoEmbed';
import Hero from '../components/publicProfile/Hero';
import Measurables from '../components/publicProfile/Measurables';
import Highlights from '../components/publicProfile/Highlights';
import Camps from '../components/publicProfile/Camps';
import Rail from '../components/publicProfile/Rail';
import StickyCta from '../components/publicProfile/StickyCta';
import '../components/Layout.css';
import '../components/Editorial.css';

export default function PublicProfilePage() {
  const params = useParams();
  const [searchParams] = useSearchParams();
  // Emailed/texted share links carry ?s=<token>; it records that the link was opened.
  const shareToken = searchParams.get('s');
  // Both /athlete/:uid/:profileUrl and the legacy /athlete/:profileUrl.
  const profileUrl = params.profileUrl || params.uid;
  // `authLoading` is true until the stored token has been exchanged for a user.
  // Deriving isCoach before then makes a signed-in coach's first paint say
  // "Coach Login" / "See Full Profile" and then flip once auth settles, and the
  // CTA hrefs flip with it — so the coach-specific copy waits.
  const { user, loading: authLoading } = useAuth();
  // Already normalized by the provider, and seeded with FOOTBALL_FALLBACK, so
  // this is safe to use before the catalog fetch resolves.
  const { catalog } = useCatalog();

  const [athlete, setAthlete] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const ctaRef = useRef(null);

  const isCoach = canAccessAthleteDetail(user && user.role);

  const handleShare = async () => {
    const uid = athlete?.uid || '0';
    const pUrl = athlete?.profile_url || profileUrl;
    const url = `${window.location.origin}/athlete/${uid}/${pUrl}`;
    if (navigator.share) {
      try { await navigator.share({ title: `${athlete?.name || 'Athlete'} | ProZpects`, url }); } catch (e) { /* cancelled */ }
    } else {
      try {
        await navigator.clipboard.writeText(url);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (e) { /* fallback */ }
    }
  };

  useEffect(() => {
    if (!shareToken) return;
    // Fire and forget: the endpoint always answers 204 and a failure must never reach the visitor.
    try {
      Promise.resolve(fetch(`${window.location.origin}/api/public/athlete-share/${encodeURIComponent(shareToken)}/open`, { method: 'POST' })).catch(() => {});
    } catch (e) { /* ignored */ }
  }, [shareToken]);

  useEffect(() => {
    // A visitor can navigate away mid-fetch (e.g. hit back before the public
    // profile resolves). Without this flag the late response still lands on
    // an unmounted page's setState — React error #300 in the console and,
    // worse, a stale athlete/error flashing onto whatever page loaded next.
    // Matches the same guard AthleteByUidRedirect uses in App.js.
    let cancelled = false;
    document.title = 'Athlete Profile | ProZpects';
    fetch(`${window.location.origin}/api/athletes/public/${profileUrl}`)
      .then(res => {
        if (!res.ok) throw new Error('Profile not found');
        return res.json();
      })
      .then(data => {
        if (cancelled) return;
        setAthlete(data);
        document.title = `${data.name} | ProZpects`;
      })
      .catch(() => { if (!cancelled) setError('Profile not found or not available.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [profileUrl]);

  // Held on authLoading as well as the profile fetch. With no stored token
  // AuthContext resolves on its first effect pass with no network call, so an
  // anonymous visitor waits one render; a signed-in coach gets the coach copy
  // and the coach hrefs on the FIRST paint instead of watching "Coach Login"
  // flip to "Dashboard" a moment later.
  if (loading || authLoading) {
    return <div className="ed-pp"><div className="loading-spinner">Loading...</div></div>;
  }

  if (error) return (
    <div className="ed-pp">
      <div className="ed-pp__clamp ed-pp__error">
        <div className="ed-cap ed-cap--flush">
          <span className="ed-cap__label">Profile Not Found</span>
        </div>
        <p>{error}</p>
      </div>
    </div>
  );

  const a = athlete;
  const { first, last } = displayName(a);
  // buildMeasurableGroups is the single source of truth for "does this
  // athlete have any measurables" — Measurables.js calls the same function
  // to decide whether to render, so re-deriving the answer here from flat
  // fields would risk disagreeing with it.
  const hasMeasurables = buildMeasurableGroups(a, catalog).length > 0;
  const hasVideos = a.videos && a.videos.length > 0;
  const hasCamps = a.camps && a.camps.length > 0;
  // The statement section is statement OR personal video, matching the page
  // this replaced. They are independent fields: an athlete can record an intro
  // video and never write a word, and gating the video on the statement both
  // dropped the video and told that athlete their profile was empty.
  const hasStatement = Boolean(a.personal_statement);
  const hasPersonal = hasStatement || Boolean(a.personal_video_url);
  // A brand-new paid athlete who hasn't filled anything in yet still needs a
  // main column that reads as "empty," not as broken — see the empty-state
  // block below.
  const hasMainContent = hasPersonal || hasMeasurables || hasVideos || hasCamps;

  return (
    <div className="ed-pp">
      <Hero
        athlete={a}
        catalog={catalog}
        isCoach={isCoach}
        onShare={handleShare}
        copied={copied}
        ctaRef={ctaRef}
      />

      <div className="ed-pp__body">
        <main className="ed-pp__main">
          {hasPersonal && (
            <section className="ed-pp__card ed-pp__statement">
              <div className="ed-cap ed-cap--flush">
                {/* Video-only athletes get an honest cap rather than a
                    "Personal Statement" heading over a film embed. */}
                <span className="ed-cap__label">
                  {hasStatement ? 'Personal Statement' : 'Personal Video'}
                </span>
              </div>
              {/* Quote and attribution travel together: the attribution line is
                  a byline for the quote, so with no quote it would dangle. */}
              {hasStatement && (
                <>
                  <p className="ed-pp__quote">“{a.personal_statement}”</p>
                  <p className="ed-pp__quote-attr">{first} {last}</p>
                </>
              )}
              {a.personal_video_url && (
                <div className="ed-pp__statement-video">
                  <VideoEmbed url={a.personal_video_url} meta={a.personal_video_meta} />
                </div>
              )}
            </section>
          )}

          <Measurables athlete={a} catalog={catalog} />
          <Highlights videos={a.videos} />
          <Camps camps={a.camps} />

          {!hasMainContent && (
            <section className="ed-pp__card ed-pp__empty">
              <div className="ed-cap ed-cap--flush">
                <span className="ed-cap__label">This Profile Is Just Getting Started</span>
              </div>
              <p className="ed-pp__empty-body">
                Measurables, film and camp history will appear here as they are added and verified.
              </p>
            </section>
          )}
        </main>

        <aside className="ed-pp__rail">
          <Rail athlete={a} isCoach={isCoach} />
        </aside>
      </div>

      <footer className="ed-pp__footer">
        <span className="ed-pp__footer-text">
          Powered by <a href="/">ProZpects</a>
          {' · '}prozpects.com/athlete/{a.uid}
        </span>
      </footer>

      <StickyCta athlete={a} isCoach={isCoach} observeRef={ctaRef} />
    </div>
  );
}
